import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import clsx from 'clsx';
import startCase from 'lodash/startCase';
import kebabCase from 'lodash/kebabCase';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { MessageBar, TooltipHost } from 'office-ui-fabric-react';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import Module from '../../components/Module';
import ModuleContentList from '../../components/ModuleContentList';
import SearchBar from '../../components/SearchBar';
import { fetchBundles, searchBundles } from '../../actions/bundles';
import styles from './BundleListTab.style';
import BundlesList from '../../components/BundlesList/BundlesList';
import { bundleStatus, formatDateString } from '../../utils';
import appStyles from '../../App.style';

const BundleListTab = () => {
  const dispatch = useDispatch();
  const [sortAscending, setSortAscending] = useState(false);
  const [sortColumn, setSortColumn] = useState();
  const [loading, setLoading] = useState(true);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNoBundles, setShowNoBundles] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const observer = useRef();
  const bundles = useSelector(state =>
    get(state, 'pagination.bundles.results', [])
      .map(bundleId => get(state, `entities.bundles.${bundleId}`))
      .sort((a, b) =>
        // eslint-disable-next-line no-nested-ternary
        a[sortColumn]?.toLowerCase() > b[sortColumn]?.toLowerCase()
          ? sortAscending
            ? 1
            : -1
          : sortAscending
          ? -1
          : 1,
      ),
  );

  const searchResults = useSelector(state => Object.values(get(state, 'search.bundles', {})));

  useEffect(() => {
    async function loadBundles() {
      const response = await dispatch(fetchBundles());
      setTotalCount(get(response, `meta.totalCount`, 0));
      setLoading(false);
      setShowNoBundles(response.meta.totalCount === 0);
    }
    loadBundles();
  }, [dispatch]);

  const handleColumnClick = (event, column) => {
    setSortAscending(!sortAscending);
    setSortColumn(column.fieldName);
  };

  const handleClear = () => {
    setShowSearchResults(false);
  };

  const handleSearch = async query => {
    if (!query) {
      setShowSearchResults(false);
      return;
    }
    setLoading(true);
    setShowSearchResults(true);
    await dispatch(searchBundles(query));
    setLoading(false);
  };

  const handlePageChange = async paginationData => {
    await dispatch(
      fetchBundles({
        start: paginationData.currentPageIndex * paginationData.pageLimit,
        count: paginationData.pageLimit,
      }),
    );
  };

  // load next data when last item is visible
  const lastItemRef = useCallback(
    refElem => {
      if (loading || !refElem) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (
          entries[0].isIntersecting &&
          bundles?.length < totalCount &&
          page < Math.ceil(totalCount / 20)
        ) {
          const data = { currentPageIndex: page, pageLimit: 20 };
          handlePageChange(data);
          setPage(prevPage => prevPage + 1);
        }
      });
      if (refElem) observer.current.observe(refElem);
    },
    [loading, page],
  );

  const baseColumns = [
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      ariaLabel: 'icon',
      iconName: 'CubeShape',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: (item, index) => {
        const status = bundleStatus(item.endDate, item.locked);
        return (
          <TooltipHost
            content={startCase(status)}
            calloutProps={{
              gapSpace: 0,
              target: `#${`bundleStatusIcon${kebabCase(item.bundleId)}`}`,
            }}
          >
            <div
              ref={bundles.length - 1 === index ? lastItemRef : null}
              className={clsx(styles.iconColumn, appStyles[`${status}Color`])}
            >
              <FontIcon
                iconName="LocationDot"
                ariaLabel="TextDocument"
                id={`bundleStatusIcon${kebabCase(item.bundleId)}`}
              />
            </div>
          </TooltipHost>
        );
      },
    },
    {
      key: 'name',
      name: 'Bundle Name',
      fieldName: 'bundleName',
      isSorted: sortColumn === 'bundleName',
      isSortedDescending: !sortAscending,
      minWidth: 250,
      maxWidth: 300,
      isResizable: true,
      onColumnClick: handleColumnClick,
    },
    {
      key: 'createdBy',
      name: 'Created By',
      fieldName: 'createdBy',
      isSorted: sortColumn === 'createdBy',
      isSortedDescending: !sortAscending,
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: ({ createdBy }) => {
        return `${createdBy}`;
      },
    },
    {
      key: 'createdDate',
      name: 'Date Created',
      fieldName: 'createdTimeStamp',
      isSorted: sortColumn === 'createdTimeStamp',
      isSortedDescending: !sortAscending,
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: ({ createdTimeStamp }) => {
        return formatDateString(createdTimeStamp);
      },
    },
    {
      key: 'expiryDate',
      name: 'Expiration Date',
      fieldName: 'endDate',
      isSorted: sortColumn === 'endDate',
      isSortedDescending: !sortAscending,
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: ({ endDate }) => {
        return formatDateString(endDate);
      },
    },
  ];

  return (
    <>
      {showNoBundles && (
        <div className={styles.resultsMessage}>
          <MessageBar>
            <Text as="h4" block>
              No Product Bundles
            </Text>
            <Text as="p" block className={styles.noResultsSentence}>
              No product bundles have been added.
            </Text>
          </MessageBar>
        </div>
      )}
      {/* If someone searched but got 0 results, hide the list */}
      {bundles.length !== 0 && (
        <Module>
          <SearchBar
            placeholder="Search bundles"
            ariaLabel="Search"
            underlined
            onChange={handleSearch} // when you type
            onClear={handleClear} // when you click X
            data-testid="search-input"
            className={styles.search}
          />
          <ModuleContentList>
            <BundlesList
              columns={baseColumns}
              items={showSearchResults ? searchResults : bundles}
              className={styles.scrollablePane}
              totalRecords={showSearchResults ? searchResults.length : totalCount}
              shimmered
              enableShimmer={loading}
              noBorderBottom
            />
          </ModuleContentList>
        </Module>
      )}
    </>
  );
};

export default BundleListTab;
